import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import PropTypes from 'prop-types';

const StatCard = ({ title, value, percentage, subtitle, valueColor }) => {
	const theme = useTheme();

	return (
		<Box
			sx={{
				backgroundColor: theme.palette.background.paper,
				padding: '16px 18px',
				borderRadius: '8px',
				boxShadow: '0 1px 5px rgba(0, 0, 0, 0.1)',
				border: `1px solid ${theme.palette.divider}`,
				minWidth: '180px',
				flex: 1,
			}}
		>
			<Typography
				variant='caption'
				sx={{
					fontSize: '11px',
					fontWeight: 550,
					color: theme.palette.text.secondary,
					textTransform: 'uppercase',
					letterSpacing: '0.7px',
					display: 'block',
					mb: 1,
				}}
			>
				{title}
			</Typography>

			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
				<Typography
					variant='h4'
					sx={{
						fontSize: '26px',
						fontWeight: 600,
						color: valueColor || '#000000',
						lineHeight: 1,
					}}
				>
					{value.toLocaleString()}
				</Typography>

				{percentage && (
					<Box
						sx={{
							backgroundColor: '#e6f9ef',
							borderRadius: '4px',
							padding: '1px 7px',
							display: 'inline-flex',
							alignItems: 'center',
							mr: 0,
						}}
					>
						<Typography
							variant='body2'
							sx={{
								fontSize: '16px',
								fontWeight: 600,
								color: '#1d8348',
							}}
						>
							{percentage}
						</Typography>
					</Box>
				)}

				{percentage && (
					<Tooltip
						title='Percentage of fully synced jobs out of all jobs across all users in this tenant (Synced ÷ Total Jobs).'
						arrow
						placement='top'
					>
						<IconButton
							size='small'
							sx={{
								backgroundColor: '#f5f5f5',
								color: '#666666',
								width: '14px',
								height: '14px',
								'&:hover': {
									backgroundColor: '#e0e0e0',
								},
							}}
						>
							<QuestionMarkIcon
								sx={{
									fontSize: '9px',
								}}
							/>
						</IconButton>
					</Tooltip>
				)}
			</Box>

			<Typography
				variant='body2'
				sx={{
					fontSize: '11px',
					color: theme.palette.text.secondary,
					lineHeight: '16px',
					letterSpacing: '0.25px',
				}}
			>
				{subtitle}
			</Typography>
		</Box>
	);
};

StatCard.propTypes = {
	title: PropTypes.string.isRequired,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	percentage: PropTypes.string,
	subtitle: PropTypes.string.isRequired,
	valueColor: PropTypes.string,
};

const AdminEmailJobStatusStatistics = ({ jobStatistics }) => {
	const stats = [
		{
			title: 'TOTAL JOBS',
			value: jobStatistics.total_jobs_count,
			subtitle: 'All time',
			valueColor: '#5a4fcf',
		},
		{
			title: 'SYNCED',
			value: jobStatistics.synced_jobs_count,
			percentage: `${jobStatistics.synced_jobs_percentage}%`,
			subtitle: 'Synced to CRM',
			valueColor: '#1d8348',
		},
		{
			title: 'INGESTED',
			value: jobStatistics.ingested_jobs_count,
			subtitle: 'Emails pulled, pending sync',
			valueColor: '#1565c0',
		},
		{
			title: 'IN PROGRESS',
			value: jobStatistics.in_progress_jobs_count,
			subtitle: 'Ingesting or syncing now',
			valueColor: '#b7770d',
		},
		{
			title: 'FAILED JOBS',
			value: jobStatistics.failed_jobs_count,
			subtitle: 'Ingestion or sync failures',
			valueColor: '#c0392b',
		},
	];

	return (
		<Box
			sx={{
				display: 'flex',
				gap: 2,
				mb: 2,
			}}
		>
			{stats.map((stat, index) => (
				<StatCard
					key={index}
					title={stat.title}
					value={stat.value}
					percentage={stat.percentage}
					subtitle={stat.subtitle}
					valueColor={stat.valueColor}
				/>
			))}
		</Box>
	);
};

AdminEmailJobStatusStatistics.propTypes = {
	jobStatistics: PropTypes.object.isRequired,
};

export default AdminEmailJobStatusStatistics;
